import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Input, EmptyState, StatusBadge } from "../components/ui-kit";
import { ArrowLeft, ShoppingCart, IndianRupee, MapPin, Sprout, UserPlus, Truck } from "lucide-react";
import { toast } from "sonner";
import { thisMonthIST } from "../lib/datefmt";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

function KpiCard({ icon: Icon, label, value, sub, tone = "moss", testid }) {
  const toneMap = {
    moss: "bg-[#1E3F20]/10 text-[#1E3F20]",
    ochre: "bg-[#DDA15E]/20 text-[#8B5A24]",
    olive: "bg-[#A3B18A]/30 text-[#3E5133]",
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center ${toneMap[tone]}`}>
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">{label}</div>
          <div className="font-heading text-2xl font-black text-[#1A2E1C] leading-tight mt-1">{value}</div>
          {sub && <div className="text-xs text-[#5C6B5E] mt-0.5">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

export default function TeamMemberDetailPage() {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(searchParams.get("period") || thisMonthIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/team/member/${userId}?period=${period}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // Intentional dep set: refetch only when userId/period change. `api` is a
    // module-level stable singleton; adding state closures here would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, period]);

  const changePeriod = (val) => {
    setPeriod(val);
    const sp = new URLSearchParams(searchParams);
    sp.set("period", val);
    setSearchParams(sp, { replace: true });
  };

  if (loading && !data) return <div className="p-6 text-sm text-[#5C6B5E]">Loading…</div>;
  if (!data) return (
    <div className="p-6">
      <EmptyState title="No data" description="This user has no records visible to you." />
    </div>
  );

  const { member, kpis, recent_orders, recent_collections, recent_sessions, recent_demos, recent_leads } = data;

  return (
    <div>
      <PageHeader
        title={member?.name || "Member"}
        subtitle={`${(member?.role || "").replace(/_/g, " ")}${member?.email ? ` · ${member.email}` : ""}${member?.phone ? ` · ${member.phone}` : ""}`}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-sm text-[#5C6B5E] hover:text-[#1E3F20]"
              data-testid="back-to-team"
            >
              <ArrowLeft className="w-4 h-4" /> Team
            </button>
            <Input
              type="month"
              value={period}
              onChange={(e) => changePeriod(e.target.value)}
              className="w-44"
              data-testid="member-period"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={ShoppingCart}
          label="Orders (period)"
          value={num(kpis.orders_count_month)}
          sub={`${num(kpis.orders_approved_month)} approved · ${money(kpis.orders_value_month)}`}
          testid="member-kpi-orders"
        />
        <KpiCard
          icon={IndianRupee}
          label="Collections"
          value={money(kpis.collections_amount_month)}
          sub={`${num(kpis.collections_count_month)} entries`}
          tone="ochre"
          testid="member-kpi-collections"
        />
        <KpiCard
          icon={MapPin}
          label="Field days"
          value={`${num(kpis.field_days_month)}`}
          sub={`${num(kpis.km_covered_month)} km covered`}
          tone="olive"
          testid="member-kpi-field"
        />
        <KpiCard
          icon={Sprout}
          label="Demos & Leads (all-time)"
          value={`${num(kpis.demos_total)} / ${num(kpis.leads_total)}`}
          sub="demos / leads"
          testid="member-kpi-demos"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentListCard
          title="Recent Orders"
          icon={ShoppingCart}
          rows={recent_orders}
          testid="member-recent-orders"
          emptyText="No orders yet"
          renderRow={(o) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold truncate">{o.customer_name}</div>
                <StatusBadge status={o.status} />
              </div>
              <div className="flex items-center justify-between text-xs text-[#5C6B5E]">
                <span>{fmtDate(o.date)}</span>
                <span className="font-semibold text-[#1A2E1C]">{money(o.total)}</span>
              </div>
            </>
          )}
        />
        <RecentListCard
          title="Recent Collections"
          icon={IndianRupee}
          rows={recent_collections}
          testid="member-recent-collections"
          emptyText="No collections yet"
          renderRow={(c) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold truncate">{c.customer_name}</div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#5C6B5E]">{c.status}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-[#5C6B5E]">
                <span>{fmtDate(c.date)} · {c.mode}</span>
                <span className="font-semibold text-[#1A2E1C]">{money(c.amount)}</span>
              </div>
            </>
          )}
        />
        <RecentListCard
          title="Recent Field Sessions"
          icon={MapPin}
          rows={recent_sessions}
          testid="member-recent-sessions"
          emptyText="No field activity yet"
          renderRow={(s) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold uppercase text-[11px] tracking-wider">{s.mode?.replace("_", " ") || "session"}</div>
                <span className="text-xs text-[#5C6B5E]">{fmtDate(s.date_key || s.start_time)}</span>
              </div>
              <div className="text-xs text-[#5C6B5E]">
                {s.mode === "field_day" ? `${num(s.total_km)} km` : ""}
                {s.working_with_user_name ? ` · with ${s.working_with_user_name}` : ""}
                {s.remarks ? ` · ${s.remarks}` : ""}
              </div>
            </>
          )}
        />
        <RecentListCard
          title="Recent Demonstrations"
          icon={Sprout}
          rows={recent_demos}
          testid="member-recent-demos"
          emptyText="No demos yet"
          renderRow={(d) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold truncate">{d.farmer_name}</div>
                <span className="text-xs text-[#5C6B5E]">{fmtDate(d.demo_date)}</span>
              </div>
              <div className="text-xs text-[#5C6B5E]">{d.product_name} · {d.crop || "—"}</div>
            </>
          )}
        />
        <RecentListCard
          title="Recent Leads"
          icon={UserPlus}
          rows={recent_leads}
          testid="member-recent-leads"
          emptyText="No leads yet"
          renderRow={(l) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold truncate">{l.name}</div>
                <span className="text-xs text-[#5C6B5E]">{l.status || "new"}</span>
              </div>
              <div className="text-xs text-[#5C6B5E]">{l.mobile || l.source || ""}</div>
            </>
          )}
        />
      </div>
    </div>
  );
}

function RecentListCard({ title, icon: Icon, rows, renderRow, testid, emptyText }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="p-4 border-b border-[#E5E2DC] flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#1E3F20]" />
        <div className="font-heading text-base font-bold">{title}</div>
        <span className="ml-auto text-xs text-[#5C6B5E]">{rows?.length || 0}</span>
      </div>
      {!rows?.length ? (
        <div className="p-6 text-center text-xs text-[#5C6B5E]">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-[#E5E2DC]">
          {rows.slice(0, 8).map((r, i) => (
            <li key={r.id || i} className="px-4 py-3 space-y-1">
              {renderRow(r)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
